/**
 * Durate del time tracking, sempre in minuti.
 *
 * Le due funzioni sono l'inversa l'una dell'altra: quello che `formatMinutes` mostra,
 * `parseDuration` lo sa rileggere. Stanno qui e non in un componente perché le usano sia il
 * tracker sia la riga della singola voce, e un import incrociato fra i due creerebbe un ciclo.
 */

/** 390 → "6h 30m". Zero e valori assenti diventano "—". */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || minutes === 0) return '—';

  const negative = minutes < 0;
  const abs = Math.abs(Math.round(minutes));
  const hours = Math.floor(abs / 60);
  const mins = abs % 60;

  let out: string;
  if (hours === 0) out = `${mins}m`;
  else if (mins === 0) out = `${hours}h`;
  else out = `${hours}h ${mins}m`;

  // Segno meno tipografico (U+2212): accanto ai numeri si legge meglio del trattino da tastiera
  // e non si confonde con un separatore.
  return negative ? `−${out}` : out;
}

/**
 * "1h 30m", "90m", "1,5h", "90" → minuti. Restituisce 0 se non interpretabile.
 *
 * Accetta anche il modo in cui la gente scrive davvero (Barf Italia, 15/09/2026: "il valore
 * inserito nel campo tempo non viene salvato" era un formato non riconosciuto): "1:30",
 * "1.30" e "1,30" come ore:minuti, "2 ore", "1 ora", "30 min", "30 minuti", "1h30",
 * "1 h 30 m", "1.5" e "1,5" senza unita' come ore. Un intero da solo resta minuti ("90").
 */
export function parseDuration(input: string): number {
  const s = input.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!s) return 0;

  const H = '(?:h|o|ora|ore)';
  const M = '(?:m|min|minuti|minuto)';

  // "1:30", "1.30", "1,30" → ore:minuti (i minuti devono essere due cifre sotto 60)
  const clock = s.match(/^(\d+)[:.,]([0-5]\d)$/);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);

  // "1h 30m", "1h30", "1 ora 30", "1 h 30 min"
  const composite = s.match(new RegExp(`^(\\d+) ?${H} ?(\\d+) ?${M}?$`));
  if (composite) return Number(composite[1]) * 60 + Number(composite[2]);

  // "2h", "1,5h", "2 ore", "1 ora", "0.5 o"
  const hours = s.match(new RegExp(`^(\\d+(?:[.,]\\d+)?) ?${H}$`));
  if (hours) return Math.round(Number(hours[1].replace(',', '.')) * 60);

  // "45m", "30 min", "30 minuti"
  const minutes = s.match(new RegExp(`^(\\d+) ?${M}$`));
  if (minutes) return Number(minutes[1]);

  // "1.5" / "1,5" senza unita': ore. Un intero da solo ("90") resta in minuti.
  const decimal = s.match(/^(\d+)[.,](\d+)$/);
  if (decimal) return Math.round(Number(`${decimal[1]}.${decimal[2]}`) * 60);

  const plain = s.match(/^\d+$/);
  if (plain) return Number(s);

  return 0;
}
